import { useRef, useCallback } from 'react';
import * as Cesium from 'cesium';
import {
  buildSampledPosition,
  buildKeplerianSampledPosition,
  type KeplerianOrbitParams,
} from '../../../lib/orbitPropagator';
import type { SatelliteGroupDataType } from '../../../../../modelling/types';
import { getColourArray } from '../../../../../../utils/commonFunctions';
import {
  ACCENT,
  CLOSE_SATELLITE_MODEL_URI,
  CLOSE_SATELLITE_MODEL_DISTANCE_M,
  MU,
  PREVIEW_COLOR,
} from '../constants';
import {
  buildBackendLlaSampledPosition,
  buildEphemerisSampledPosition,
  buildTleSampledPosition,
} from '../utils/orbitUtils';
import type { MapSatellite } from '../types';

export function useSatelliteOrbits(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  modelClockWindowRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  satOrbitActiveMap: React.MutableRefObject<Record<string, boolean>>,
  layersRef: React.MutableRefObject<any>,
  orbitLeadTimeRef: React.MutableRefObject<number>,
  orbitTrailTimeRef: React.MutableRefObject<number>,
  clearGroup: (group: string) => void,
  getDefaultModelEpoch: () => number,
  editingSatelliteIdsRef?: React.MutableRefObject<Set<string>>,
) {
  const drawSatelliteGroupOrbits = useCallback((groups: SatelliteGroupDataType[]) => {
    const v = viewerRef.current;
    const ds = sharedDataSourceRef.current;
    if (!v || !ds) return;
    const existingOrbitIds = entityGroupMap.current.get('satellite-orbits') ?? [];
    const wasAnimating = existingOrbitIds.length > 0 && v.clock.shouldAnimate;
    const currentSec = Cesium.JulianDate.toDate(v.clock.currentTime).getTime() / 1000;
    clearGroup('satellite-orbits');

    const win = modelClockWindowRef.current;
    const clockStartSec = win?.startEpoch ?? Date.now() / 1000;
    const clockEndSec = win?.endEpoch;
    const defaultSec = getDefaultModelEpoch();
    const isCurrentSecValid = Number.isFinite(currentSec) && (win ? (currentSec >= win.startEpoch && currentSec <= win.endEpoch) : currentSec > 0);
    const displaySec = isCurrentSecValid ? currentSec : defaultSec;
    const now = new Date(displaySec * 1000);
    const ids: string[] = [];
    let longestPeriod = 5_400;

    if (!wasAnimating && (!v.clock.currentTime || !Cesium.JulianDate.toDate(v.clock.currentTime).getTime() || !isCurrentSecValid)) {
      v.clock.currentTime = Cesium.JulianDate.fromDate(now);
    }

    groups.forEach((group) => {
      group.satellites.forEach((rawSat) => {
        const sat = rawSat as MapSatellite;
        const tleLines = sat.tleLines;
        const ephemeris = sat.ephemeris;
        const backendOrbitTrack = sat.backendOrbitTrack;
        const op = sat.orbitParameters;

        let sampledPos: Cesium.SampledPositionProperty;
        let T: number;

        const backendBuilt = backendOrbitTrack?.orbitPoints?.length ? buildBackendLlaSampledPosition(backendOrbitTrack) : null;
        const ephemerisBuilt = !backendBuilt && ephemeris?.length ? buildEphemerisSampledPosition(ephemeris) : null;
        if (backendBuilt) {
          sampledPos = backendBuilt.position;
          T = backendBuilt.periodSec;
        } else if (ephemerisBuilt) {
          sampledPos = ephemerisBuilt.position;
          T = ephemerisBuilt.periodSec;
        } else if (tleLines) {
          const periodGuess = 5400;
          const satStartDate = new Date((win?.startEpoch ?? defaultSec - periodGuess) * 1000);
          const spanSec = Math.max(periodGuess * 4, (clockEndSec ?? defaultSec + periodGuess * 4) - (satStartDate.getTime() / 1000));
          const built = buildTleSampledPosition(tleLines.line1, tleLines.line2, satStartDate, Math.ceil(spanSec / periodGuess), 120);
          sampledPos = built.position;
          T = built.periodSec;
        } else {
          if (!op?.semiMajorAxis || op.semiMajorAxis <= 0) return;
          const n = Math.sqrt(MU / Math.pow(op.semiMajorAxis, 3));
          T = (2 * Math.PI) / n;
          const satStartDate = new Date((win?.startEpoch ?? defaultSec - T * 2) * 1000);
          const spanSec = Math.max(T * 4, (clockEndSec ?? defaultSec + T * 4) - (satStartDate.getTime() / 1000));
          sampledPos = buildKeplerianSampledPosition(
            {
              semiMajorAxis: op.semiMajorAxis,
              eccentricity: op.eccentricity,
              inclination: op.inclination,
              RAAN: op.RAAN,
              AOP: op.AOP,
              TA: op.TA,
              epoch: op.epoch,
            },
            satStartDate,
            Math.ceil(spanSec / T),
            480,
          );
          sampledPos.forwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
          sampledPos.backwardExtrapolationType = Cesium.ExtrapolationType.HOLD;
        }

        if (T > longestPeriod) longestPeriod = T;

        satOrbitActiveMap.current[sat.id] = sat.active !== false;

        let orbitColor = ACCENT;
        if (sat.propagationStatus === 'preview' || sat.propagationStatus === 'propagating') {
          orbitColor = PREVIEW_COLOR;
        } else if (sat.orbitColour && !sat.orbitColour.includes('#')) {
          try {
            const arr = getColourArray(sat.orbitColour);
            orbitColor = new Cesium.Color(arr[0] / 255, arr[1] / 255, arr[2] / 255, 1.0);
          } catch { /* keep ACCENT */ }
        }

        const satId = sat.id;
        const isBeingEdited = () => editingSatelliteIdsRef?.current?.has(satId) ?? false;
        const isSatelliteVisible = () => {
          if (isBeingEdited()) return false;
          return layersRef.current.satellites && satOrbitActiveMap.current[satId] !== false;
        };
        const isCloseSatelliteModelView = (time?: Cesium.JulianDate) => {
          if (!isSatelliteVisible()) return false;
          const position = sampledPos.getValue(time ?? v.clock.currentTime);
          if (!position) return false;
          return Cesium.Cartesian3.distance(v.camera.positionWC, position) <= CLOSE_SATELLITE_MODEL_DISTANCE_M;
        };

        const entity = ds.entities.add({
          id: satId,
          name: sat.name,
          position: sampledPos,
          orientation: new Cesium.VelocityOrientationProperty(sampledPos),
          point: {
            pixelSize: 6,
            color: orbitColor,
            outlineColor: Cesium.Color.WHITE,
            outlineWidth: 1,
            show: new Cesium.CallbackProperty(
              (time?: Cesium.JulianDate) => isSatelliteVisible() && !isCloseSatelliteModelView(time),
              false,
            ),
          },
          model: {
            uri: CLOSE_SATELLITE_MODEL_URI,
            scale: 1.0,
            minimumPixelSize: 28,
            maximumScale: 220000,
            silhouetteColor: orbitColor.withAlpha(0.9),
            silhouetteSize: 1.0,
            show: new Cesium.CallbackProperty(
              (time?: Cesium.JulianDate) => isCloseSatelliteModelView(time),
              false,
            ),
          },
          label: {
            text: sat.name,
            font: '10px "Courier New", monospace',
            fillColor: Cesium.Color.WHITE,
            outlineColor: Cesium.Color.BLACK,
            outlineWidth: 2,
            style: Cesium.LabelStyle.FILL_AND_OUTLINE,
            verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
            pixelOffset: new Cesium.Cartesian2(0, -12),
            eyeOffset: new Cesium.Cartesian3(0, 0, 500000),
            show: new Cesium.CallbackProperty(
              () => layersRef.current.labels && isSatelliteVisible(),
              false,
            ),
          },
          path: {
            show: new Cesium.CallbackProperty(
              () => {
                if (isBeingEdited()) return false;
                return layersRef.current.trails && satOrbitActiveMap.current[satId] !== false;
              },
              false,
            ),
            material: new Cesium.ColorMaterialProperty(orbitColor),
            width: 2.2,
            leadTime: new Cesium.CallbackProperty(() => orbitLeadTimeRef.current, false),
            trailTime: new Cesium.CallbackProperty(() => orbitTrailTimeRef.current, false),
            resolution: 60,
          },
        });

        if (entity.id) ids.push(entity.id);
      });
    });

    entityGroupMap.current.set('satellite-orbits', ids);

    if (ids.length > 0) {
      const clockStopSec = clockEndSec ?? defaultSec + longestPeriod * 2;
      v.clock.startTime = Cesium.JulianDate.fromDate(new Date(clockStartSec * 1000));
      v.clock.stopTime = Cesium.JulianDate.fromDate(new Date(clockStopSec * 1000));
      v.clock.clockRange = Cesium.ClockRange.CLAMPED;
      if (!Number.isFinite(v.clock.multiplier) || v.clock.multiplier === 0) v.clock.multiplier = 1;
      v.clock.shouldAnimate = wasAnimating;
    }
  }, [clearGroup, getDefaultModelEpoch]);

  const setSatOrbitVisible = useCallback((satId: string, visible: boolean) => {
    satOrbitActiveMap.current[satId] = visible;
    viewerRef.current?.scene.requestRender();
  }, []);

  const setGroupOrbitVisible = useCallback((satIds: string[], visible: boolean) => {
    satIds.forEach((id) => { satOrbitActiveMap.current[id] = visible; });
    viewerRef.current?.scene.requestRender();
  }, []);

  return {
    drawSatelliteGroupOrbits,
    setSatOrbitVisible,
    setGroupOrbitVisible,
  };
}
